import {
	Paper,
	Box,
	FormControlLabel,
	Switch,
	useMediaQuery
} from "@mui/material";
import LogoSpinner from '../../../components/LogoSpinner/LogoSpinner';
import { GeoJSON, MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import React, { useEffect, useState } from "react";
import il_sinirlari from "../../../assets/geojson/tr-cities-utf8.json";
import * as L from "leaflet";
import { LatLngExpression } from "leaflet";
import { addFeatureEvents, getFeatureStyle } from "../../../helpers/mapStyles";
import { markerClusterPieChart } from "../../../helpers/markerClusterPieChart";
import { useSelector } from "../../../redux/store";
import Legend from './Legend';
import DistrictModal from './DistrictModal';
import EventPinModal, { EventItem } from './EventPinModal';
import 'leaflet.heat';
import CacheService from '../../../services/CacheService';

interface IMapProps {
	events: any,
	filters: any,
	setFilters: any,
	pins: any,
	setPins: any,
	isLoading: boolean
}

interface CityEventCount {
	city: string;
	count: number;
	averageDailyCount: number;
	lastDayCount: number;
	logThreatLevel: number;
	threatLevel: number;
	events?: Array<{
		latitude: number;
		longitude: number;
		eventTypeId: string;
		description: string;
		date: string;
	}>;
}

interface DistrictEventCount {
	district: string;
	count: number;
	averageDailyCount: number;
	lastDayCount: number;
	logThreatLevel: number;
	threatLevel: number;
	events?: Array<{
		latitude: number;
		longitude: number;
		eventTypeId: string;
		description: string;
		date: string;
	}>;
}

interface PinResponse {
	items: Array<{
		id: string;
		name: string;
		latitude: number;
		longitude: number;
		radiusInMeters: number;
		sector: string;
		userId: string;
	}>;
}

const customMarkerIcon = new L.Icon({
	iconUrl: '/placeholder.png', //????
	iconSize: [32, 32], // İkon boyutu: genişlik, yükseklik
	iconAnchor: [16, 32], // Haritada sabit noktası (ikonun alt ortası)
	popupAnchor: [0, -32], // Popup konumlandırması
});

// Cache anahtarları
const MAP_CACHE_KEYS = {
	PINS: 'map_pins',
	PIN_INFO: (pinId: string) => `map_pin_info_${pinId}`,
	EVENTS_COUNT: (city: string, dateStart: string, dateEnd: string, eventTypeId: string = '') =>
		`map_events_count_${city || 'all'}_${dateStart}_${dateEnd}_${eventTypeId || 'all'}`
};

// Cache süresi
const MAP_CACHE_EXPIRY = {
	SHORT: 5 * 60 * 1000,  // 5 dakika
	MEDIUM: 30 * 60 * 1000 // 30 dakika
};

const Map = (props: IMapProps) => {
	const { events, filters, setFilters, pins, setPins, isLoading } = props;
	const center: LatLngExpression = [38.9637, 35.2433];
	const [useNewColorScale, setUseNewColorScale] = useState<boolean>(true);

	// Responsive breakpoints
	const isTabletScreen = useMediaQuery('(min-width:600px) and (max-width:1199px)');

	return (
		<Paper elevation={3} sx={{
			width: '100%',
			height: isTabletScreen ? '40vh' : '75vh', // Tablet'te daha da düşük yükseklik
			overflow: 'hidden',
			position: 'relative',
			zIndex: 1,
			backgroundColor: '#f5f5f5',
			maxWidth: '100%', // Genişliği sınırla
			boxSizing: 'border-box' // Padding ve border'ı genişliğe dahil et
		}}>
			{isLoading && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'rgba(255, 255, 255, 0.7)',
						zIndex: 1000,
					}}
				>
					<LogoSpinner size={48} minHeight="auto" />
				</Box>
			)}
			<MapContainer
				center={center}
				zoom={isTabletScreen ? 6 : 6} // Tablet'te zoom seviyesini düzelttim
				style={{
					height: '100%',
					width: '100%',
					backgroundColor: '#f5f5f5',
					zIndex: 1
				}}
				dragging={true}
				touchZoom={true}
				doubleClickZoom={true}
				scrollWheelZoom={true}
				boxZoom={true}
				keyboard={true}
				zoomControl={true}
			>
				<MapComponents
					events={events}
					filters={filters}
					setFilters={setFilters}
					pins={pins}
					setPins={setPins}
					isLoading={isLoading}
					useNewColorScale={useNewColorScale}
					setUseNewColorScale={setUseNewColorScale}
				/>
			</MapContainer>
		</Paper>
	)
}

// HeatLayer için type declaration ekleyelim
declare module 'leaflet' {
	export interface HeatMapOptions {
		minOpacity?: number;
		maxZoom?: number;
		max?: number;
		radius?: number;
		blur?: number;
		gradient?: { [key: number]: string };
	}

	export interface HeatLayer extends L.Layer {
		setLatLngs(latlngs: L.LatLngExpression[]): void;
		addLatLng(latlng: L.LatLngExpression): void;
		setOptions(options: HeatMapOptions): void;
	}

	export interface HeatLayerFactory {
		(latlngs: L.LatLngExpression[], options?: HeatMapOptions): HeatLayer;
	}

	export interface LeafletStatic {
		heatLayer: HeatLayerFactory;
	}

	interface Map {
		pm?: any;
	}
}

const MapComponents = (props: IMapProps & {
	useNewColorScale: boolean;
	setUseNewColorScale: (value: boolean) => void;
}) => {
	const { events, filters, setFilters, isLoading, useNewColorScale, setUseNewColorScale } = props;
	const map = useMap();

	// Responsive breakpoint
	const isTabletScreen = useMediaQuery('(min-width:600px) and (max-width:1199px)');
	const [cityPins, setCityPins] = useState<Array<{
		id: string;
		name: string;
		latitude: number;
		longitude: number;
		radiusInMeters: number;
		sector: string;
		userId: string;
	}>>([]);
	const [eventPinModalOpen, setEventPinModalOpen] = useState(false);
	const [selectedPinEvents, setSelectedPinEvents] = useState<EventItem[]>([]);
	const [selectedPinCoordinates, setSelectedPinCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
	const [districtModalOpen, setDistrictModalOpen] = useState(false);
	const [selectedDistrict, setSelectedDistrict] = useState("");
	const [showPieChart, setShowPieChart] = useState<boolean>(true);
	const [showDistrictFill, setShowDistrictFill] = useState<boolean>(true);
	const [showHeatmap, setShowHeatmap] = useState<boolean>(false);
	const [districtBounds, setDistrictBounds] = useState<any>(null);

	const center: LatLngExpression = [38.9637, 35.2433];

	const { districtBounds: ilce_sinirlari } = useSelector(state => state.app)
	// const drawnItems = L.featureGroup().addTo(map);

	const calculateTValue = (count: number): number => {
		return Math.log(count + 1) * 3; // Basit bir risk hesaplaması
	}

	const interpretTValue = (t: number): string => {
		if (t < 3) return 'Düşük';
		if (t >= 3 && t <= 6) return 'Orta';
		if (t >= 6 && t <= 12) return 'Yüksek';
		return 'Çok Yüksek';
	}

	var ilSinirlari: any = il_sinirlari;

	// GeoJSON ("Istanbul") ile DB ("İstanbul","Hakkâri","Çanakkale" vb.) eşleşmesi
	// Türkçe özel + aksan + circumflex karakterlerin tamamını normalize eder
	const normalizeCity = (name: string): string => {
		if (!name) return '';
		return name
			// Türkçe özel karakterler
			.replace(/İ/g, 'I').replace(/ı/g, 'i')
			.replace(/Ş/g, 'S').replace(/ş/g, 's')
			.replace(/Ğ/g, 'G').replace(/ğ/g, 'g')
			.replace(/Ü|Û/g, 'U').replace(/ü|û/g, 'u')
			.replace(/Ö/g, 'O').replace(/ö/g, 'o')
			.replace(/Ç/g, 'C').replace(/ç/g, 'c')
			// Circumflex / düzeltme işareti: â→a, î→i, ê→e, ô→o, û→u (Hakkâri vb.)
			.replace(/Â/g, 'A').replace(/â/g, 'a')
			.replace(/Î/g, 'I').replace(/î/g, 'i')
			.replace(/Ê/g, 'E').replace(/ê/g, 'e')
			.replace(/Ô/g, 'O').replace(/ô/g, 'o')
			// Unicode NFD ile geri kalan tüm aksan karakterlerini düşür (è, é, ñ vb.)
			.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
			.toUpperCase();
	};

	const eventCounts: any = {}
	if (!filters.city && !!events.cityEventCounts) {
		events.cityEventCounts.forEach((city: CityEventCount) => {
			// Normalize key ile kaydet: "İstanbul" → "ISTANBUL"
			eventCounts[normalizeCity(city.city)] = {
				city: city.city,
				count: city.count,
				averageDailyCount: city.averageDailyCount,
				lastDayCount: city.lastDayCount,
				logThreatLevel: city.logThreatLevel,
				threatLevel: city.threatLevel,
				t: calculateTValue(city.count),
				tInterpretation: interpretTValue(calculateTValue(city.count)),
				events: events.events?.items?.filter((item: any) => item.city === city.city).map((item: any) => ({
					latitude: item.latitude,
					longitude: item.longitude,
					eventTypeId: item.eventType.id,
					description: item.description,
					date: item.date
				}))
			}
		});
	} else if (filters.city && !!events.districtEventCounts) {
		events.districtEventCounts.forEach((district: DistrictEventCount) => {
			eventCounts[district.district] = {
				city: district.district,
				count: district.count,
				averageDailyCount: district.averageDailyCount,
				lastDayCount: district.lastDayCount,
				logThreatLevel: district.logThreatLevel,
				threatLevel: district.threatLevel,
				t: calculateTValue(district.count),
				tInterpretation: interpretTValue(calculateTValue(district.count)),
				events: events.events?.items?.filter((item: any) => item.district === district.district).map((item: any) => ({
					latitude: item.latitude,
					longitude: item.longitude,
					eventTypeId: item.eventType.id,
					description: item.description,
					date: item.date
				}))
			}
		});
	}

	const CityBounds = () => {
		map.setView(center, isTabletScreen ? 5 : 6)

		// Marker'ları temizle
		map.eachLayer((layer) => {
			if (layer instanceof L.MarkerClusterGroup) {
				map.removeLayer(layer);
			}
		});

		return ilSinirlari.features.map((feature: any) => {
			const city = feature.properties.name;
			// Normalize ile ara: "Istanbul" → "ISTANBUL" = "İstanbul" normalize edilmişi
			const eventData = eventCounts[normalizeCity(city)];
			const uniqueKey = feature.id || `city-${city}`;

			return (
				<GeoJSON
					key={uniqueKey}
					data={feature}
					style={getFeatureStyle(eventData, filters.eventTypeId, useNewColorScale)}
					onEachFeature={(feature: any, layer: any) => {
						layer.on({
							click: () => {
								const bounds = layer.getBounds();
								map.fitBounds(bounds, { padding: [50, 50] });
								setFilters({ ...filters, city });
							}
						});
						addFeatureEvents(layer, eventData)
					}}
				/>
			);
		})
	}

	// İl seçildiğinde ilçe sınırlarını yükle
	useEffect(() => {
		if (filters.city) {
			fetch('/simplified_ilce_sinirlari.json')
				.then(response => response.json())
				.then(data => {
					setDistrictBounds(data);
				})
				.catch(error => {
					console.error('İlçe sınırları yüklenirken hata:', error);
				});
		}
	}, [filters.city]);

	const DistrictBounds = () => {
		// İlçe sınırları ve harita işlemleri için memoized callback
		const updateMap = React.useCallback(() => {
			if (districtBounds?.features) {
				const ilFeature = districtBounds.features.find((q: any) => q.properties.name === filters.city);

				if (ilFeature) {
					const layer = L.geoJSON(ilFeature);
					const newBounds = layer.getBounds();
					map.fitBounds(newBounds);
				}

				// Tüm katmanları temizle
				map.eachLayer((layer) => {
					if (layer instanceof L.MarkerClusterGroup) {
						map.removeLayer(layer);
					}
				});

				if (events?.events?.items?.length > 0 && showPieChart) {
					const mappedItems = events.events.items.map((item: {
						latitude: any;
						longitude: any;
						eventType: { id: any; };
						description?: string;
						date?: string;
					}) => ({
						latitude: item.latitude,
						longitude: item.longitude,
						eventTypeId: item.eventType.id,
						description: item.description,
						date: item.date
					}));

					markerClusterPieChart(
						map,
						mappedItems,
						(events, coordinates) => {
							setSelectedPinEvents(events);
							setSelectedPinCoordinates(coordinates);
							setEventPinModalOpen(true);
						}
					);
				}
			}
		}, [districtBounds, filters.city, map, events, showPieChart]);

		// Effect'i güncellenen callback ile çalıştır
		useEffect(() => {
			updateMap();
		}, [updateMap]);

		const getModifiedFeatureStyle = (eventData: any) => {
			const baseStyle = getFeatureStyle(eventData, filters.eventTypeId, useNewColorScale);
			return (feature: any) => {
				const style = baseStyle(feature);
				return showDistrictFill ? style : { ...style, fillOpacity: 0 };
			};
		};

		// İlçe sınırlarını render et
		return (
			<React.Fragment>
				{districtBounds?.features
					.filter((q: any) => q.properties.IL_ADI === filters.city)
					.map((feature: any) => {
						const eventData = eventCounts[feature.properties.ILCE_ADI];
						const uniqueKey = `district-${feature.id || feature.properties.ILCE_ADI}`;

						return (
							<GeoJSON
								pane="tilePane"
								key={uniqueKey}
								data={feature}
								style={getModifiedFeatureStyle(eventData)}
								onEachFeature={(feature, layer) => {
									addFeatureEvents(layer, eventData);
									layer.on('click', () => {
										setSelectedDistrict(feature.properties.ILCE_ADI);
										setDistrictModalOpen(true);
									});
								}}
							/>
						);
					})}
				<DistrictModal
					open={districtModalOpen}
					onClose={() => setDistrictModalOpen(false)}
					districtName={selectedDistrict}
					filters={filters}
				/>
				{selectedPinCoordinates && (
					<EventPinModal
						open={eventPinModalOpen}
						onClose={() => setEventPinModalOpen(false)}
						events={selectedPinEvents}
						coordinates={selectedPinCoordinates}
					/>
				)}
			</React.Fragment>
		);
	}

	// Pin tıklama işlemi - reports sayfasına yönlendirme
	const handlePinClick = (pin: any) => {
		console.log('📍 Pin tıklandı:', pin);
		// Reports sayfasına yönlendirme yap
		window.location.href = `/reports/${pin.id}`;
	};

	// Pin'in seçili şehir sınırları içinde olup olmadığını kontrol et
	const isPinInSelectedCity = (pin: any): boolean => {
		if (!filters.city) {
			return false;
		}

		// İlce sınırları yerine ilSinirlari kullan (bu her zaman yüklü)
		if (!ilSinirlari) {
			return false;
		}

		// İl sınırlarını bul - ilSinirlari kullan
		const selectedCityFeature = ilSinirlari.features.find(
			(feature: any) => feature.properties.name === filters.city
		);

		if (!selectedCityFeature) {
			return false;
		}

		// Pin koordinatlarını kontrol et
		const point = L.latLng(pin.latitude, pin.longitude);
		const cityPolygon = L.geoJSON(selectedCityFeature);
		const cityBounds = cityPolygon.getBounds();
		const isInCity = cityBounds.contains(point);

		return isInCity;
	};

	// Pin'leri localStorage'dan yüklemek için fonksiyon
	const loadPinsFromLocalStorage = () => {
		try {
			const storedPins = localStorage.getItem('pins');

			if (storedPins) {
				const parsedPins = JSON.parse(storedPins);

				// Eğer parsedPins bir array ise doğrudan kullan, değilse data, items property'si var mı kontrol et
				const pinsArray = Array.isArray(parsedPins) ? parsedPins : (parsedPins.data || parsedPins.items || []);

				if (pinsArray.length > 0) {
					// İl sınırları içindeki pinleri filtrele
					const filteredPins = pinsArray.filter((pin: any) => isPinInSelectedCity(pin));
					setCityPins(filteredPins);
				} else {
					setCityPins([]);
				}
			} else {
				setCityPins([]);
			}
		} catch (error) {
			console.error('localStorage\'dan pinler okunurken hata:', error);
			setCityPins([]);
		}
	};

	// İl sınırları değiştiğinde
	useEffect(() => {
		if (ilce_sinirlari) {
			// İl sınırları yüklendi
		}
	}, [ilce_sinirlari]);

	// filters.city değiştiğinde pin'leri yükle
	useEffect(() => {
		if (filters.city) {
			// ilce_sinirlari'nı beklemeden ilSinirlari ile işlem yap
			loadPinsFromLocalStorage();
		} else {
			// İl seçili değilse pinleri temizle
			setCityPins([]);
		}
	}, [filters.city]); // ilce_sinirlari'nı dependency'den çıkar

	// Filters değiştiğinde veya temizlendiğinde markerları temizle
	useEffect(() => {
		// Sadece markerCluster ve heat layer'ları temizle, custom pinleri koru
		map.eachLayer((layer) => {
			// MarkerClusterGroup veya heat layer ise kaldır
			if (layer instanceof L.MarkerClusterGroup ||
				// @ts-ignore - HeatLayer kontrolü için
				layer._heat) {
				map.removeLayer(layer);
			}
			// Custom marker'ları kontrol et ve koru
			if (layer instanceof L.Marker) {
				// Eğer marker customMarkerIcon kullanıyorsa (yani custom pin ise) koru
				const markerIcon = (layer.getIcon() as L.Icon);
				if (markerIcon.options.iconUrl !== customMarkerIcon.options.iconUrl) {
					map.removeLayer(layer);
				}
			}
		});

		// Eğer şehir seçili değilse veya events yoksa, marker'ları ekleme
		if (!filters.city || !events?.events?.items) {
			return;
		}

		// Şehir seçili ve events varsa
		if (events?.events?.items?.length > 0) {
			if (showPieChart) {
				const mappedItems = events.events.items.map((item: {
					latitude: any;
					longitude: any;
					eventType: { id: any; };
					description?: string;
					date?: string;
				}) => ({
					latitude: item.latitude,
					longitude: item.longitude,
					eventTypeId: item.eventType.id,
					description: item.description,
					date: item.date
				}));

				markerClusterPieChart(
					map,
					mappedItems,
					(events, coordinates) => {
						setSelectedPinEvents(events);
						setSelectedPinCoordinates(coordinates);
						setEventPinModalOpen(true);
					}
				);
			} else if (showHeatmap) {
				const heatData = events.events.items.map((item: any) => [
					item.latitude,
					item.longitude,
					1
				]);

				// @ts-ignore
				const heatLayer = L.heatLayer(heatData, {
					radius: 35,
					blur: 30,
					maxZoom: 7,
					max: 1,
					gradient: {
						0.4: 'blue',
						0.6: 'lime',
						0.8: 'yellow',
						1.0: 'red'
					}
				}).addTo(map);

				// @ts-ignore
				map.heatLayer = heatLayer;
			}
		}
	}, [filters, events, showPieChart, showHeatmap, map]);

	// Switch handler'ları geri ekleyelim
	const handlePieChartSwitch = (checked: boolean) => {
		setShowPieChart(checked);
		if (checked) {
			if (events?.events?.items && events.events.items.length > 0) {
				const mappedItems = events.events.items.map((item: {
					latitude: any;
					longitude: any;
					eventType: { id: any; };
					description?: string;
					date?: string;
				}) => ({
					latitude: item.latitude,
					longitude: item.longitude,
					eventTypeId: item.eventType.id,
					description: item.description,
					date: item.date
				}));

				markerClusterPieChart(map, mappedItems);
			}
		} else {
			map.eachLayer((layer) => {
				if (layer instanceof L.MarkerClusterGroup) {
					map.removeLayer(layer);
				}
			});
		}
	};

	const handleDistrictFillSwitch = (checked: boolean) => {
		setShowDistrictFill(checked);
	};

	const handleHeatmapSwitch = (checked: boolean) => {
		setShowHeatmap(checked);
		if (checked) {
			if (showPieChart) {
				setShowPieChart(false);
				map.eachLayer((layer) => {
					if (layer instanceof L.MarkerClusterGroup) {
						map.removeLayer(layer);
					}
				});
			}

			if (events?.events?.items && events.events.items.length > 0) {
				const heatData = events.events.items.map((item: any) => [
					item.latitude,
					item.longitude,
					1
				]);

				// @ts-ignore
				const heatLayer = L.heatLayer(heatData, {
					radius: 25,
					blur: 15,
					maxZoom: 10,
					max: 1,
					gradient: {
						0.4: 'blue',
						0.6: 'lime',
						0.8: 'yellow',
						1.0: 'red'
					}
				}).addTo(map);

				// @ts-ignore
				map.heatLayer = heatLayer;
			}
		} else {
			// @ts-ignore
			if (map.heatLayer) {
				// @ts-ignore
				map.removeLayer(map.heatLayer);
				// @ts-ignore
				map.heatLayer = null;
			}
		}
	};

	return (
		<React.Fragment>
			<TileLayer
				url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				attribution=""
				detectRetina={true}
				opacity={1}
				noWrap={true}
				keepBuffer={2}
				updateWhenIdle={false}
				updateWhenZooming={true}
				updateInterval={200}
			/>
			{!isLoading && (
				<>
					<Legend
						selectedEventType={filters.eventTypeId}
						useNewColorScale={useNewColorScale}
						onColorScaleChange={setUseNewColorScale}
					/>
					{filters.city && (
						<div
							style={{
								position: 'absolute',
								top: '10px',
								left: '10px',
								zIndex: 1000,
								backgroundColor: 'white',
								padding: window.innerWidth < 900 ? '8px' : '10px',
								borderRadius: '8px',
								boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
								fontSize: window.innerWidth < 900 ? '0.8rem' : '1rem',
							}}
						>
							<FormControlLabel
								control={
									<Switch
										checked={showDistrictFill}
										onChange={(e) => handleDistrictFillSwitch(e.target.checked)}
										disabled={showHeatmap}
										color="primary"
									/>
								}
								label="İlçe Renklendirme"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={showPieChart}
										onChange={(e) => {
											if (e.target.checked && showHeatmap) {
												setShowHeatmap(false);
											}
											handlePieChartSwitch(e.target.checked);
										}}
										disabled={showHeatmap}
										color="primary"
									/>
								}
								label="Olay Dağılımı"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={showHeatmap}
										onChange={(e) => {
											if (e.target.checked) {
												setShowPieChart(false);
												setShowDistrictFill(false);
											}
											handleHeatmapSwitch(e.target.checked);
										}}
										color="primary"
									/>
								}
								label="Isı Haritası"
							/>
						</div>
					)}
					{!filters.city ? <CityBounds /> : <DistrictBounds />}

					{/* Şehir pinlerini göster - Sadece il seçiliyse */}
					{filters.city && cityPins.map(pin => (
						<Marker
							key={pin.id}
							position={[pin.latitude, pin.longitude]}
							icon={customMarkerIcon}
							eventHandlers={{
								click: () => handlePinClick(pin)
							}}
						/>
					))}
				</>
			)}
		</React.Fragment>
	)
}

export default Map;